import { Check } from "lucide-react";

import type {
  LifecycleStep,
  TrackingLifecycle,
} from "@/utils/publicTracking/lifecycle";
import { cn } from "@/utils/utils";

interface LifecycleStepperProps {
  lifecycle: TrackingLifecycle;
}

/**
 * 4-step delivery lifecycle: green check = complete, orange dot = current,
 * empty = upcoming, with a date under each step and hairline connectors.
 */
export function LifecycleStepper({ lifecycle }: LifecycleStepperProps) {
  const { steps } = lifecycle;
  return (
    <div className="flex items-start">
      {steps.map((step, index) => {
        const isLast = index === steps.length - 1;
        return (
          <div
            key={step.key}
            className={cn("flex items-start", !isLast && "flex-1")}
          >
            <StepNode step={step} />
            {!isLast ? (
              <div
                className={cn(
                  "mt-3 h-px flex-1",
                  step.state === "complete" ? "bg-emerald-600" : "bg-border",
                )}
              />
            ) : null}
          </div>
        );
      })}
    </div>
  );
}

function StepNode({ step }: { step: LifecycleStep }) {
  return (
    <div className="flex w-20 shrink-0 flex-col items-center gap-1.5 text-center">
      <span
        className={cn(
          "flex size-6 items-center justify-center rounded-full text-[10px] font-semibold",
          step.state === "complete" && "bg-emerald-600 text-white",
          step.state === "current" &&
            "bg-orange-500 text-white ring-4 ring-orange-500/15",
          step.state === "upcoming" &&
            "border-border bg-card text-muted-foreground border",
        )}
      >
        {step.state === "complete" ? (
          <Check className="size-3.5" />
        ) : step.state === "current" ? (
          <span className="size-2 rounded-full bg-white" />
        ) : null}
      </span>
      <span
        className={cn(
          "text-xs leading-tight font-medium",
          step.state === "upcoming"
            ? "text-muted-foreground"
            : "text-foreground",
        )}
      >
        {step.label}
      </span>
      <span className="text-muted-foreground text-[11px]">
        {step.dateLabel ?? "—"}
      </span>
    </div>
  );
}
